import { Schema } from "@effect/schema";
import { Context, Record, pipe } from "effect";
import { CamelFromKebab } from "./string";
import { Chain } from "viem";
import { arbitrum, mainnet as ethereum, optimism, base } from "viem/chains";
import { anvil, local } from "./viem";
import { Usd } from "./usd";
import { Percent } from "./percent";
import { dual, satisfies } from "effect/Function";

export const ProtocolKey = Schema.Literal(
  "mainnetLegacy",
  "mainnetLlama",
  "mainnetGrand",
  "arbitrum",
  "optimism",
  "base",
  "lrtbase",
  "local",
  "anvil"
).annotations({ identifier: "ProtocolKey" });

export type ProtocolKey = typeof ProtocolKey.Type;
export const protocolKeys = ProtocolKey.literals;
export const isProtocolKey = Schema.is(ProtocolKey);

interface GenericProtocol {
  label: string;
  chain: Chain;
}

export const baseProtocolMap = satisfies<
  Record<ProtocolKey, GenericProtocol>
>()({
  mainnetLegacy: {
    label: "Ethereum Legacy",
    chain: ethereum,
  },
  mainnetLlama: {
    label: "LLAMA edition",
    chain: ethereum,
  },
  mainnetGrand: {
    label: "Ethereum",
    chain: ethereum,
  },
  arbitrum: {
    label: "Arbitrum",
    chain: arbitrum,
  },
  optimism: {
    label: "Optimism",
    chain: optimism,
  },
  base: {
    label: "Base",
    chain: base,
  },
  lrtbase: {
    label: "BTCfi",
    chain: base,
  },
  local: {
    label: "Local",
    chain: local,
  },
  anvil: {
    label: "Anvil",
    chain: anvil,
  },
} as const);

type ProtocolMap = {
  [K in keyof typeof baseProtocolMap]: (typeof baseProtocolMap)[K] & { key: K };
};

export const protocolMap = pipe(
  baseProtocolMap,
  Record.map((value, key) => ({ key, ...value }))
) as ProtocolMap;

export type Protocol = (typeof protocolMap)[ProtocolKey];

export function getProtocol(protocolKey: ProtocolKey): Protocol {
  return protocolMap[protocolKey];
}

export class ProtocolKeyService extends Context.Tag("ProtocolKeyService")<
  ProtocolKeyService,
  ProtocolKey
>() {}

export const ProtocolKeySlug = Schema.compose(CamelFromKebab, ProtocolKey);

/**
 * Production protocol keys have sub-graphs and redis support, unlike local or anvil
 */
export const ProdProtocolKey = ProtocolKey.pipe(
  Schema.pickLiteral(
    "arbitrum",
    "mainnetLegacy",
    "mainnetLlama",
    "mainnetGrand",
    "optimism",
    "base",
    "lrtbase"
  )
).annotations({ identifier: "ProdProtocolKey" });

export type ProdProtocolKey = typeof ProdProtocolKey.Type;
export const prodProtocolKeys = ProdProtocolKey.literals;
export const isProdProtocolKey = Schema.is(ProdProtocolKey);

export class ProdProtocolKeyService extends Context.Tag(
  "ProdProtocolKeyService"
)<ProdProtocolKeyService, ProdProtocolKey>() {}

export const GRAPH_ENDPOINT: Record<ProdProtocolKey, string> = {
  mainnetLegacy:
    "https://gateway-arbitrum.network.thegraph.com/api/41d8e9d9c63d206f22b98602980156de/deployments/id/QmeDLbKHYypURMRigRxSspUm8w5zrDfXc3Skw2PiDxqCFu",
  mainnetLlama:
    "https://gateway-arbitrum.network.thegraph.com/api/41d8e9d9c63d206f22b98602980156de/deployments/id/QmU6W7w6gAjDoE637hAVLjizp2cc7RYtUQsEzKnGp26VVa",
  mainnetGrand:
    "https://gateway-arbitrum.network.thegraph.com/api/41d8e9d9c63d206f22b98602980156de/deployments/id/QmcYjagkzh7KiFKoLzeXs5SGynz7iMPVRnSdVPYiCUrLGq",
  arbitrum:
    "https://gateway-arbitrum.network.thegraph.com/api/67a8618c8f7e739d765013de92bd75cd/deployments/id/QmYtYfMeMcS2YBugkQ5fh6CcidQrKL3PHUEc7HnyYCW6Gc",
  optimism:
    "https://gateway-arbitrum.network.thegraph.com/api/67a8618c8f7e739d765013de92bd75cd/deployments/id/Qmb4EVvFzGefGp3br5ozffT9yJ1vKptbawypqMPd7MctBh",
  base: 
    "https://gateway-arbitrum.network.thegraph.com/api/67a8618c8f7e739d765013de92bd75cd/deployments/id/QmTqzhfBYLdVNA3P5BCuYyanKvGAEUjWVZUieAe6dcRCDv",
  lrtbase:
    "https://gateway-arbitrum.network.thegraph.com/api/67a8618c8f7e739d765013de92bd75cd/deployments/id/QmYHjei6Z71YcuSnk5KuXXv1tr5aqfUiL2gQMukeA9B1eF",
};

export type ProtocolState = typeof ProtocolState.Type;
export type ProtocolStateEncoded = typeof ProtocolState.Encoded;

export const ProtocolState = Schema.Struct({
  quotePriceUsd: Usd,
  rewardTokenPriceUsd: Usd,
  protocolShareFee: Percent,
}).annotations({ identifier: "ProtocolState" });

export class ProtocolStateService extends Context.Tag("ProtocolStateService")<
  ProtocolStateService,
  ProtocolState
>() {}

export const isRuntimeProtocolKey: {
  (runtimeProtocolKeys: ProtocolKey[]): (self: ProtocolKey) => boolean;
  (self: ProtocolKey, runtimeProtocolKeys: ProtocolKey[]): boolean;
} = dual(2, (self: ProtocolKey, runtimeProtocolKeys: ProtocolKey[]) =>
  runtimeProtocolKeys.includes(self)
);
